import type React from 'react'
import {Link} from '@primer/react'
import {useSoftNavigateTo} from '../../hooks/use-soft-navigate-to'
import styles from './Header.module.css'
import {searchPath} from '@github-ui/paths'
import type {SearchKind, SearchKindCount} from '../../hooks/use-search-result-counts'
import {getValueText} from '@github-ui/count'

export default function Header({
  resultKind,
  searchKinds,
  isLoadingCounters,
}: {
  resultKind: SearchKind
  searchKinds: SearchKindCount[]
  isLoadingCounters: boolean
}) {
  let description: React.ReactNode = `Try one of the tips below to find more ${resultKind.readableNamePlural}`

  if (!isLoadingCounters) {
    const [first, second] = searchKinds
      .filter(kind => kind.name !== resultKind.name && kind.total && kind.total > 0)
      .sort((a, b) => (b.total || 0) - (a.total || 0))
    let resultsInfo

    if (first && second) {
      resultsInfo = (
        <>
          <TabSwitcherLink key={first.name} kind={first} /> and <TabSwitcherLink key={second.name} kind={second} />
        </>
      )
    } else if (first) {
      resultsInfo = <TabSwitcherLink key={first.name} kind={first} />
    } else {
      resultsInfo = null
    }

    if (resultsInfo) {
      description = (
        <>However we found {resultsInfo} that matched your search query. Alternatively try one of the tips below.</>
      )
    } else {
      description = <>You could try one of the tips below.</>
    }
  }

  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerContent}>
        <h3 className={styles.heading}>Your search did not match any {resultKind.readableNamePlural}</h3>
        <p className={styles.description}>{description}</p>
      </div>
    </div>
  )
}

function TabSwitcherLink({kind}: {kind: SearchKindCount}) {
  const [searchTypeHref, navigateToSearchType] = useSoftNavigateTo(searchPath, undefined, {
    p: null,
    page_token: null,
    type: kind.name,
    l: null,
    s: null,
    o: null,
  })
  let kindName = kind.total === 1 ? kind.readableName : kind.readableNamePlural

  if (kindName === 'code') {
    kindName = kind.total === 1 ? 'code result' : 'code results'
  }

  return (
    <Link onClick={(e: React.SyntheticEvent) => navigateToSearchType(e)} href={searchTypeHref}>
      {getValueText(kind.total || 0)} {kindName.toLowerCase()}
    </Link>
  )
}
